import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Document, Page, pdfjs } from 'react-pdf';
import { styles } from '@sdir/sds';

// eslint-disable-next-line max-len
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
// will fix cdn

export const FileViewer: FC<{
  file: File;
  url?: string;
  type?: string;
  documentDisplayClass?: string;
  handleDocumentError?: (err: Error) => void;
}> = ({ file, url, type, documentDisplayClass, handleDocumentError }) => {
  const fileUrl = useMemo(() => {
    if (url) return url;
    return URL.createObjectURL(file);
  }, [file, url]);

  const fileType = useMemo(() => {
    const documentType = file.type || type;
    if (!documentType) {
      const fileName = file.name || '';
      if (fileName.includes('.png') || fileName.includes('.PNG')) return 'image/png';
      if (fileName.includes('.pdf')) return 'application/pdf';
      if (fileName.includes('.jpg') || fileName.includes('.jpeg')) return 'image/jpeg';
    }
    return documentType;
  }, [file, type]);

  return (
    <DocumentDisplay>
      <div className={documentDisplayClass}>
        {(fileType === 'image/png' || fileType === 'image/jpeg') && (
          <Img key="application-img" src={fileUrl} alt="application" />
        )}
        {fileType === 'application/pdf' && (
          <PdfViewer fileUrl={fileUrl} handleDocumentError={handleDocumentError} />
        )}
      </div>
    </DocumentDisplay>
  );
};

type PdfViewerProps = {
  fileUrl: string;
  handleDocumentError?: (err: Error) => void;
};

const PdfViewer: React.FC<PdfViewerProps> = ({ fileUrl, handleDocumentError }) => {
  const [renderWidth, setRenderWidth] = useState<number>();
  const [numPages, setNumPages] = useState<number>(0);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleDocumentLoaded = useCallback(
    (doc: { numPages: number }) => setNumPages(doc.numPages),
    []
  );

  useEffect(() => {
    const updateRenderWidth = () => {
      if (!wrapperRef.current) return;
      setRenderWidth(wrapperRef.current.getBoundingClientRect().width);
    };
    updateRenderWidth();
    window.addEventListener('resize', updateRenderWidth);
    return () => window.removeEventListener('resize', updateRenderWidth);
  }, []);

  return (
    <div ref={wrapperRef}>
      <Document
        file={fileUrl}
        onLoadSuccess={handleDocumentLoaded}
        onLoadError={handleDocumentError}
      >
        {Array.from(new Array(numPages), (_el, index) => (
          <StyledPage key={`page_${index + 1}`} pageNumber={index + 1} width={renderWidth} />
        ))}
      </Document>
    </div>
  );
};

const StyledPage = styled(Page)`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Img = styled.img`
  width: 100%;
  height: auto;
`;

const DocumentDisplay = styled.div`
  width: 100%;
  padding: 1rem;
  background: ${styles.colors.primary5};

  canvas {
    width: 100% !important;
    height: auto !important;
  }
`;
